// The viewer's time machine, under the time presets: a date and time to type
// or pick (the visitor's own time zone), a scrubber that moves the clock up to
// a week either way while it is held, a button that runs the clock
// backwards, and jumps to now, the next equinox or solstice, the next full
// and new Moon, the next eclipse and a birthday. The share sheet gets a link
// to the exact view (src/ui/viewlink.js) and a picture of the moment to
// share. main.js hands over the clock and the scene through api (see
// initTimeMachine's call there).
import { nextSeason, nextMoonPhase, nextEclipse } from '../sky/timejumps.js';
import { encodeView } from './viewlink.js';
import { distanceFromEarthKm, formatMillionKm, DISTANCE_BODIES } from '../site/readouts.js';
import { issState } from '../iss/orbit.js';
import { explore } from '../analytics.js';

const JD_UNIX = 2440587.5;
const jdToMs = (jd) => (jd - JD_UNIX) * 86400000;
const msToJd = (ms) => ms / 86400000 + JD_UNIX;
// The ephemeris is fitted for 1800 to 2050; the field allows a little past.
const MIN_YEAR = 1800;
const MAX_YEAR = 2200;
// The scrubber's reach either way, in hours, and its curve (fine near the
// middle: a tenth of the way is under two hours).
const SCRUB_HOURS = 24 * 7;
const BIRTHDAY_KEY = 'ss.birthday';

const CSS = `
.tm { display: flex; flex-direction: column; gap: 6px; margin-top: 2px; }
.tm-when { display: grid; grid-template-columns: 1fr 36px; gap: 4px; }
.tm-date {
  min-width: 0; height: 34px; padding: 0 10px;
  background: #141922; border: none; border-radius: 8px;
  color: #ece7da; font: 0.76rem 'Inter', sans-serif; font-variant-numeric: tabular-nums; color-scheme: dark;
}
.tm-date:focus { outline: none; }
.tm-date:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 2px; }
.tm-btn {
  display: flex; align-items: center; justify-content: center; min-height: 32px; padding: 0 4px;
  background: #141922; border: none; border-radius: 6px;
  color: #a8a396; font: 500 0.7rem 'Inter', sans-serif; cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
}
.tm-btn:hover:not(:disabled) { color: #ece7da; background: #1b212c; }
.tm-btn:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 1px; }
.tm-btn.is-active { color: #ece7da; background: #15302a; }
.tm-btn:disabled { opacity: 0.4; cursor: default; }
.tm-scrub { display: flex; flex-direction: column; gap: 4px; }
.tm-scrub input { width: 100%; margin: 0; height: 18px; -webkit-appearance: none; appearance: none; background: none; cursor: pointer; }
.tm-scrub input::-webkit-slider-runnable-track { height: 4px; border-radius: 2px; background: #232a36; }
.tm-scrub input::-webkit-slider-thumb { -webkit-appearance: none; width: 14px; height: 14px; margin-top: -5px; border-radius: 50%; background: #ece7da; }
.tm-scrub input::-moz-range-track { height: 4px; border-radius: 2px; background: #232a36; }
.tm-scrub input::-moz-range-thumb { width: 14px; height: 14px; border: none; border-radius: 50%; background: #ece7da; }
.tm-scrub input:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 2px; }
.tm-scrub-label { font: 0.68rem 'Inter', sans-serif; color: #a8a396; text-align: center; min-height: 0.9em; }
.tm-scrub.is-dragging .tm-scrub-label { color: #ece7da; }
.tm-jumps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; }
.tm-birthday { display: grid; grid-template-columns: 1fr 64px 44px; gap: 4px; align-items: center; }
.tm-birthday[hidden] { display: none; }
.tm-birthday input { min-width: 0; height: 32px; padding: 0 8px; background: #141922; border: none; border-radius: 6px; color: #ece7da; font: 0.72rem 'Inter', sans-serif; color-scheme: dark; }
.tm-birthday input:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 1px; }
.tm-birthday-note { grid-column: 1 / -1; margin: 0; font: 0.68rem 'Inter', sans-serif; color: #a8a396; }
.tm-status { margin: 0; min-height: 0; font: 0.7rem 'Inter', sans-serif; line-height: 1.4; color: #a8a396; }
.tm-status:empty { display: none; }
.tm-status button { all: unset; cursor: pointer; color: #ece7da; text-decoration: underline; text-underline-offset: 2px; }
#controls-panel .time-presets.is-reverse .time-preset.active { background: #2b2213; color: #f3e1c2; }
.tm-reverse.is-active { color: #f3e1c2; background: #2b2213; }
.share-sheet-actions .tm-share-btn { grid-column: 1 / -1; }
.tm-share-preview { display: block; width: 100%; margin-top: 10px; border-radius: 8px; }
.tm-share-preview[hidden] { display: none; }
`;

function el(tag, attrs = {}, text) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null) continue;
    if (k === 'class') node.className = v;
    else node.setAttribute(k, v);
  }
  if (text != null) node.textContent = text;
  return node;
}

const pad = (n) => String(n).padStart(2, '0');
// A Date as the value of a datetime-local input, in local time.
function toLocalInput(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const dateTimeFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const longFmt = new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
const dayFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

function readBirthday() {
  try { return localStorage.getItem(BIRTHDAY_KEY) || null; } catch { return null; }
}
function writeBirthday(value) {
  try { localStorage.setItem(BIRTHDAY_KEY, value); } catch { /* private window */ }
}

// "+2 d 4 h", "-45 min": the scrubber's offset while it is held.
function offsetText(hours) {
  const sign = hours < 0 ? '-' : '+';
  const h = Math.abs(hours);
  if (h < 1) return `${sign}${Math.round(h * 60)} min`;
  const whole = Math.round(h);
  const d = Math.floor(whole / 24);
  const rest = whole - d * 24;
  if (d === 0) return `${sign}${rest} h`;
  return rest ? `${sign}${d} d ${rest} h` : `${sign}${d} d`;
}

export function initTimeMachine(api) {
  const presets = document.getElementById('time-presets');
  if (!presets || document.getElementById('time-machine')) return null;
  document.head.appendChild(el('style', { id: 'time-machine-css' }, CSS));

  const root = el('div', { class: 'tm', id: 'time-machine' });
  const when = el('div', { class: 'tm-when' });
  const dateInput = el('input', {
    type: 'datetime-local', id: 'tm-date', class: 'tm-date', step: '60',
    min: `${MIN_YEAR}-01-01T00:00`, max: `${MAX_YEAR}-12-31T23:59`,
    'aria-label': 'Date and time, in your local time',
    title: 'Go to any date and time (your local time)',
  });
  const reverseBtn = el('button', { type: 'button', class: 'tm-btn tm-reverse', id: 'tm-reverse', 'aria-pressed': 'false', 'aria-label': 'Run the clock backwards', title: 'Run the clock backwards' });
  reverseBtn.innerHTML = '<svg width="12" height="10" viewBox="0 0 12 10" aria-hidden="true"><path d="M6 1 1 5l5 4V1zm5 0L6 5l5 4V1z" fill="currentColor"/></svg>';
  when.append(dateInput, reverseBtn);

  const scrub = el('div', { class: 'tm-scrub' });
  const scrubInput = el('input', { type: 'range', id: 'tm-scrub', min: '-1000', max: '1000', step: '1', value: '0', 'aria-label': 'Hold and drag to move the clock up to a week either way' });
  const scrubLabel = el('span', { class: 'tm-scrub-label', id: 'tm-scrub-label' }, 'Drag to move up to a week either way');
  scrub.append(scrubInput, scrubLabel);

  const jumps = el('div', { class: 'tm-jumps', role: 'group', 'aria-label': 'Jump to' });
  const jumpDefs = [
    ['now', 'Now', 'Back to the sky right now'],
    ['season', 'Equinox', 'The next equinox or solstice'],
    ['full', 'Full Moon', 'The next full Moon'],
    ['new', 'New Moon', 'The next new Moon'],
    ['eclipse', 'Eclipse', 'The next solar or lunar eclipse'],
    ['birthday', 'Birthday', 'The sky on your birthday'],
  ];
  const jumpBtns = {};
  for (const [key, label, title] of jumpDefs) {
    const b = el('button', { type: 'button', class: 'tm-btn', 'data-jump': key, title }, label);
    jumpBtns[key] = b;
    jumps.appendChild(b);
  }

  const bdayForm = el('form', { class: 'tm-birthday', hidden: '' });
  const bdayDate = el('input', { type: 'date', 'aria-label': 'Your birthday', min: `${MIN_YEAR}-01-01`, max: `${MAX_YEAR}-12-31`, required: '' });
  const bdayTime = el('input', { type: 'time', 'aria-label': 'Time of day (optional)', value: '21:00' });
  const bdayGo = el('button', { type: 'submit', class: 'tm-btn' }, 'Go');
  const bdayNote = el('p', { class: 'tm-birthday-note' }, 'Kept on this device only. Add the time you were born if you know it.');
  bdayForm.append(bdayDate, bdayTime, bdayGo, bdayNote);

  const status = el('p', { class: 'tm-status', id: 'tm-status', 'aria-live': 'polite' });
  root.append(when, scrub, jumps, bdayForm, status);
  presets.insertAdjacentElement('afterend', root);

  // ---- Keeping the controls in step with the clock --------------------------
  let editing = false;
  dateInput.addEventListener('focus', () => { editing = true; });
  dateInput.addEventListener('blur', () => { editing = false; refresh(); });

  let labelsJD = null;
  function refresh() {
    const jd = api.getJD();
    const d = new Date(jdToMs(jd));
    if (!editing && !Number.isNaN(d.getTime())) {
      const v = toLocalInput(d);
      if (dateInput.value !== v) dateInput.value = v;
    }
    const reverse = api.isReverse();
    reverseBtn.classList.toggle('is-active', reverse);
    reverseBtn.setAttribute('aria-pressed', reverse ? 'true' : 'false');
    presets.classList.toggle('is-reverse', reverse);
    // The labels only change with the date, so they are worked out again
    // only when the clock has moved by more than a day.
    if (labelsJD === null || Math.abs(jd - labelsJD) > 1) {
      labelsJD = jd;
      const season = nextSeason(jd);
      if (season) jumpBtns.season.textContent = /solstice/.test(season.name) ? 'Solstice' : 'Equinox';
      const eclipse = nextEclipse(jd);
      jumpBtns.eclipse.disabled = !eclipse;
      jumpBtns.eclipse.title = eclipse ? `${eclipse.title}, ${dayFmt.format(new Date(eclipse.at))}` : 'No eclipse in the calendar after this date';
    }
  }
  refresh();
  setInterval(refresh, 1000);

  let sayTimer = null;
  function say(text, action) {
    clearTimeout(sayTimer);
    sayTimer = setTimeout(() => { status.textContent = ''; }, action ? 20000 : 9000);
    status.textContent = text;
    if (action) {
      status.append(' ');
      const b = el('button', { type: 'button' }, action.label);
      b.addEventListener('click', action.run);
      status.appendChild(b);
    }
  }

  function jumpTo(jd, text, action) {
    api.setJD(jd);
    say(text, action);
    refresh();
  }

  // ---- The date field --------------------------------------------------------
  dateInput.addEventListener('change', () => {
    const ms = new Date(dateInput.value).getTime();
    if (!Number.isFinite(ms)) return;
    const year = new Date(ms).getFullYear();
    if (year < MIN_YEAR || year > MAX_YEAR) { say(`Pick a date between ${MIN_YEAR} and ${MAX_YEAR}.`); return; }
    jumpTo(msToJd(ms), `The sky on ${dateTimeFmt.format(new Date(ms))}.`);
    explore('time_travel', 'date', { via: 'date' });
  });

  // ---- The scrubber: hold and drag, it springs back on release ---------------
  let scrubAnchor = null;
  let scrubSpeed = 0;
  function scrubStart() {
    if (scrubAnchor !== null) return;
    scrubAnchor = api.getJD();
    scrubSpeed = api.getSpeedState();
    api.pause();
    scrub.classList.add('is-dragging');
  }
  function scrubMove() {
    if (scrubAnchor === null) scrubStart();
    const v = Number(scrubInput.value) / 1000;
    const hours = Math.sign(v) * v * v * SCRUB_HOURS;
    api.setJD(scrubAnchor + hours / 24);
    scrubLabel.textContent = Math.abs(hours) < 1 / 60 ? 'Drag to move up to a week either way' : offsetText(hours);
  }
  function scrubEnd() {
    if (scrubAnchor === null) return;
    scrubAnchor = null;
    if (scrubInput.value !== '0') explore('time_travel', 'scrub', { via: 'scrub' });
    scrubInput.value = '0';
    scrub.classList.remove('is-dragging');
    scrubLabel.textContent = 'Drag to move up to a week either way';
    api.restoreSpeedState(scrubSpeed);
    say(`The sky on ${dateTimeFmt.format(new Date(jdToMs(api.getJD())))}.`);
    refresh();
  }
  scrubInput.addEventListener('pointerdown', scrubStart);
  scrubInput.addEventListener('input', scrubMove);
  scrubInput.addEventListener('change', scrubEnd);
  scrubInput.addEventListener('pointerup', scrubEnd);
  scrubInput.addEventListener('pointercancel', scrubEnd);
  scrubInput.addEventListener('blur', scrubEnd);

  // ---- Reverse ---------------------------------------------------------------
  reverseBtn.addEventListener('click', () => {
    api.setReverse(!api.isReverse());
    refresh();
    explore('time_travel', 'reverse', { via: 'reverse' });
  });

  // ---- Jumps -----------------------------------------------------------------
  function birthdayJump(value) {
    const [date, time] = String(value).split('T');
    const ms = new Date(`${date}T${time || '21:00'}`).getTime();
    if (!Number.isFinite(ms)) return false;
    explore('time_travel', 'birthday', { via: 'birthday' });
    jumpTo(msToJd(ms), `Your birthday, ${dateTimeFmt.format(new Date(ms))}.`, {
      label: 'Change',
      run: () => openBirthday(value),
    });
    return true;
  }
  function openBirthday(current) {
    bdayForm.hidden = false;
    const [date, time] = String(current || '').split('T');
    if (date) bdayDate.value = date;
    if (time) bdayTime.value = time;
    bdayDate.focus();
  }
  bdayForm.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!bdayDate.value) return;
    const value = `${bdayDate.value}T${bdayTime.value || '21:00'}`;
    writeBirthday(value);
    bdayForm.hidden = true;
    birthdayJump(value);
  });

  jumps.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-jump]');
    if (!btn || btn.disabled) return;
    const jd = api.getJD();
    // The birthday counts when it jumps (birthdayJump), not when it asks.
    if (btn.dataset.jump !== 'birthday') explore('time_travel', btn.dataset.jump, { via: btn.dataset.jump });
    switch (btn.dataset.jump) {
      case 'now':
        api.goNow();
        say('The sky right now.');
        refresh();
        break;
      case 'season': {
        const s = nextSeason(jd);
        if (s) jumpTo(s.jd, `${s.name}, ${dateTimeFmt.format(new Date(jdToMs(s.jd)))}.`);
        break;
      }
      case 'full':
      case 'new': {
        const p = nextMoonPhase(jd, btn.dataset.jump);
        if (p) jumpTo(p.jd, `${p.name}, ${dateTimeFmt.format(new Date(jdToMs(p.jd)))}.`);
        break;
      }
      case 'eclipse': {
        const ev = nextEclipse(jd);
        if (ev) { say(`${ev.title}, ${dayFmt.format(new Date(ev.at))}.`); api.startEclipse(ev); }
        break;
      }
      case 'birthday': {
        const saved = readBirthday();
        if (saved && birthdayJump(saved)) break;
        openBirthday(saved);
        break;
      }
      default:
        break;
    }
  });

  // ---- Share: a link to this exact view, and a picture of the moment --------
  const sheetActions = document.querySelector('#share-sheet .share-sheet-actions');
  let viewBtn = null;
  let momentBtn = null;
  let preview = null;
  if (sheetActions) {
    viewBtn = el('button', { type: 'button', class: 'share-sheet-btn primary full tm-share-btn', id: 'share-view-link' }, 'Copy link to this exact view');
    momentBtn = el('button', { type: 'button', class: 'share-sheet-btn full tm-share-btn', id: 'share-moment' }, 'Share this moment');
    sheetActions.prepend(viewBtn, momentBtn);
    // The exact view is the main action now; the body's own link stays.
    document.getElementById('share-sheet-copy-btn')?.classList.remove('primary');
    preview = el('img', { class: 'tm-share-preview', id: 'share-moment-preview', alt: '', hidden: '' });
    sheetActions.insertAdjacentElement('afterend', preview);
    viewBtn.addEventListener('click', async () => {
      const link = viewLink();
      await copyText(link);
      flash(viewBtn, 'Copied');
      api.track?.('copy_view_link');
    });
    momentBtn.addEventListener('click', () => shareMoment());
  }

  function viewLink() {
    const state = api.getViewState();
    const base = `${location.origin}${location.pathname}`;
    return state ? base + encodeView(state) : location.href;
  }

  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const t = el('textarea');
      t.value = text;
      document.body.appendChild(t);
      t.select();
      try { document.execCommand('copy'); } catch { /* nothing more to try */ }
      t.remove();
    }
  }

  function flash(btn, text) {
    const prev = btn.textContent;
    btn.textContent = text;
    setTimeout(() => { btn.textContent = prev; }, 1400);
  }

  // Where the moment is: the place under the ISS or under the camera over the
  // Earth, otherwise how far the body is from Earth.
  let whereMod = null;
  let water = null;
  async function placeText() {
    const id = api.getFocusId();
    const date = new Date(jdToMs(api.getJD()));
    let point = null;
    if (id === 'iss' && api.getSatrec()) {
      const st = issState(api.getSatrec(), date);
      if (st) point = { lat: st.lat, lon: st.lon };
    } else if (id === 'earth') {
      point = api.getEarthPointBelowCamera();
    }
    if (point) {
      try {
        if (!whereMod) {
          whereMod = await import('../site/iss/where.js');
          water = whereMod.waterMask('/marketing/flat-earth/water-2k.webp');
        }
        // The mask is a small image; give it a moment the first time.
        for (let i = 0; i < 40 && !water.ready; i++) await new Promise((r) => setTimeout(r, 50));
        const isWater = water.isWater(point.lat, point.lon);
        if (isWater !== null && isWater !== undefined) return whereMod.whereIs(point.lat, point.lon, isWater).text;
      } catch { /* fall through to coordinates */ }
      const ns = point.lat >= 0 ? 'N' : 'S';
      const ew = point.lon >= 0 ? 'E' : 'W';
      return `Over ${Math.abs(point.lat).toFixed(1)}° ${ns}, ${Math.abs(point.lon).toFixed(1)}° ${ew}`;
    }
    if (DISTANCE_BODIES.includes(id)) {
      const km = distanceFromEarthKm(id, api.getJD());
      if (Number.isFinite(km)) return `${formatMillionKm(km)} from Earth`;
    }
    return null;
  }

  async function shareMoment() {
    if (!momentBtn) return;
    momentBtn.disabled = true;
    const prev = momentBtn.textContent;
    momentBtn.textContent = 'Making the picture…';
    try {
      const name = api.getFocusName();
      const date = new Date(jdToMs(api.getJD()));
      const when = longFmt.format(date);
      const place = await placeText();
      const link = viewLink();
      const blob = await makeCard({ canvas: api.renderCanvas(), name, when, place });
      if (!blob) throw new Error('no picture');
      if (preview) {
        if (preview.src) URL.revokeObjectURL(preview.src);
        preview.src = URL.createObjectURL(blob);
        preview.alt = `${name}, ${when}`;
        preview.hidden = false;
      }
      const file = new File([blob], `solar-system-${date.toISOString().slice(0, 10)}.png`, { type: 'image/png' });
      const text = `${name}, ${when}${place ? `. ${place}` : ''}.`;
      api.track?.('share_moment');
      if (navigator.canShare && navigator.canShare({ files: [file] }) && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
        try {
          await navigator.share({ files: [file], title: `${name} in 3D`, text, url: link });
          momentBtn.textContent = prev;
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') { momentBtn.textContent = prev; return; }
        }
      }
      const a = el('a', { href: URL.createObjectURL(blob), download: file.name });
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      await copyText(link);
      momentBtn.textContent = 'Saved, link copied';
      setTimeout(() => { momentBtn.textContent = prev; }, 1800);
    } catch (err) {
      console.warn('[time machine] share failed', err);
      momentBtn.textContent = 'Could not make the picture';
      setTimeout(() => { momentBtn.textContent = prev; }, 1800);
    } finally {
      momentBtn.disabled = false;
    }
  }

  return { refresh, viewLink, shareMoment };
}

// The share card: the view as it is on screen, cropped to 1200 x 630, with a
// caption along the bottom.
export async function makeCard({ canvas, name, when, place }) {
  const W = 1200, H = 630;
  const card = document.createElement('canvas');
  card.width = W;
  card.height = H;
  const ctx = card.getContext('2d');
  ctx.fillStyle = '#05070b';
  ctx.fillRect(0, 0, W, H);
  if (canvas && canvas.width && canvas.height) {
    const s = Math.max(W / canvas.width, H / canvas.height);
    const dw = canvas.width * s, dh = canvas.height * s;
    ctx.drawImage(canvas, (W - dw) / 2, (H - dh) / 2, dw, dh);
  }
  const g = ctx.createLinearGradient(0, H * 0.48, 0, H);
  g.addColorStop(0, 'rgba(4, 6, 10, 0)');
  g.addColorStop(1, 'rgba(4, 6, 10, 0.86)');
  ctx.fillStyle = g;
  ctx.fillRect(0, H * 0.48, W, H * 0.52);
  try { await document.fonts?.load?.('600 52px Inter'); } catch { /* system font */ }
  const left = 56;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'rgba(244, 239, 224, 0.6)';
  ctx.font = "500 15px 'Orbitron', 'Inter', sans-serif";
  ctx.fillText('OUR SOLAR SYSTEM IN 3D', left, H - 160);
  ctx.fillStyle = '#f4efe0';
  ctx.font = "600 52px 'Inter', sans-serif";
  ctx.fillText(name, left, H - 102, W - left * 2);
  ctx.fillStyle = 'rgba(244, 239, 224, 0.86)';
  ctx.font = "400 24px 'Inter', sans-serif";
  ctx.fillText(when, left, H - 64, W - left * 2);
  if (place) {
    ctx.fillStyle = 'rgba(244, 239, 224, 0.62)';
    ctx.font = "400 20px 'Inter', sans-serif";
    ctx.fillText(place, left, H - 32, W - left * 2 - 260);
  }
  ctx.fillStyle = 'rgba(244, 239, 224, 0.55)';
  ctx.font = "500 17px 'Inter', sans-serif";
  ctx.textAlign = 'right';
  ctx.fillText('3dsolarsystem.online', W - left, H - 32);
  return new Promise((resolve) => card.toBlob(resolve, 'image/png'));
}
